import React, { useEffect, useRef, useState, useMemo } from 'react';
import { UserRoleEnum, UserType } from '../../types/auth';
import { RootState } from '../../redux';
import { Country } from '../../types/openApi';
import { OpenAPIService } from '../../lib/api/openApi';
import { useAppSelector } from '../../redux/hooks';
import { useLocation } from 'react-router';
import { ALLOWED_COUNTRIES } from '../../config/constants'; // Import the allowed countries

type Props = {
    id?: string;
    setFieldValue?: (fieldName: string, fieldvalue: string) => void;
    setNameOfCountry?: (fieldName: string) => void;
    countryDisabled?: boolean;
    setStateDisabled?: (key: boolean) => void;
    countryFieldValue?: string;
    setCityDisabled?: (key: boolean) => void;
    setIso2CountryString?: (key: string) => void;
    setShowSkeleton?: (key: boolean) => void;
    onCountrySelectionChange?: (value: any) => void;
    error?: any;
    hideName?: boolean;
};

const CountryDropdown: React.FC<Props> = ({
    id,
    setFieldValue,
    setNameOfCountry,
    countryDisabled,
    countryFieldValue,
    setCityDisabled,
    setStateDisabled,
    setIso2CountryString,
    setShowSkeleton,
    onCountrySelectionChange,
    error,
    hideName,
}) => {
    const [countryName, setCountryName] = useState('Select Country');
    const [filterText, setFilterText] = useState('');
    const [openDropdown, setOpenDropdown] = useState(false);
    const [countriesData, setCountriesData] = useState<Country[]>();
    const [dropdownPlacement, setDropdownPlacement] = useState<'down' | 'up'>('down');
    const [allowedCountriesData, setAllowedCountriesData] = useState<Country[]>();

    const filterInputRef = useRef<HTMLInputElement>(null);
    const countryButtonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const hasAutoSelectedRef = useRef<string>(''); // Store which country was auto-selected

    const { user } = useAppSelector((state) => state.user);
    const location = useLocation();

    const hasUser = user as UserType;

    let hasCandidate = false;
    if (location.pathname === '/edit-my-profile') {
        hasCandidate = hasUser.role === UserRoleEnum.candidate;
    }

    const filteredCountries = useMemo(() => {
        const dataToFilter = allowedCountriesData || countriesData;
        
        return dataToFilter &&
            dataToFilter
                .sort((a, b) => a.name.localeCompare(b.name))
                .filter(
                    (countries) =>
                        countries.name.toLowerCase().startsWith(filterText.toLowerCase()) ||
                        countries.id.toString().startsWith(filterText)
                );
    }, [allowedCountriesData, countriesData, filterText]);

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (
                countryButtonRef.current &&
                !countryButtonRef.current.contains(event.target as Node) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setOpenDropdown(false);
            }
        };

        window.addEventListener('click', handleOutsideClick);

        return () => {
            window.removeEventListener('click', handleOutsideClick);
        };
    }, []);

    useEffect(() => {
        const getCountries = async () => {
            const data = await OpenAPIService.fetchCountriesWithPhoneCode();
            return data;
        };

        const countries = JSON.parse(sessionStorage.getItem('countries')!);

        if (countries) {
            setCountriesData(countries);
        } else {
            (async () => {
                const result = await getCountries();
                setCountriesData(result);
            })();
        }
    }, []);

    // Handle allowed countries logic and auto-selection
    useEffect(() => {
        if (countriesData && ALLOWED_COUNTRIES && ALLOWED_COUNTRIES.length > 0) {
            // Filter countries based on allowed ISO2 codes
            const allowed = countriesData.filter((country) =>
                ALLOWED_COUNTRIES.includes(country.iso2)
            );

            setAllowedCountriesData(allowed);

            // If only one country is allowed, auto-select it
            if (allowed.length === 1) {
                const defaultCountry = allowed[0];
                const countryId = defaultCountry.id.toString();
                
                // Check if we need to auto-select (either no value or different value)
                // Reset detection: if field value is empty but we previously auto-selected, re-select
                if (!countryFieldValue || hasAutoSelectedRef.current === countryId) {
                    hasAutoSelectedRef.current = countryId;
                    
                    // Set the country name immediately
                    setCountryName(defaultCountry.name);
                    setNameOfCountry?.(defaultCountry.name);
                    setIso2CountryString?.(defaultCountry.iso2);
                    
                    // Set the field value if it's not already set
                    if (!countryFieldValue) {
                        setFieldValue?.('companyCountryCode', countryId);
                    }
                    
                    // Clear dependent fields only if no value exists
                    if (!countryFieldValue) {
                        setFieldValue?.('companyStateCode', '');
                        setFieldValue?.('companyCityCode', '');
                    }
                    
                    // CRITICAL: Enable state dropdown immediately
                    setStateDisabled?.(false);
                    setCityDisabled?.(true);
                    
                    // Trigger the callback - NO setTimeout needed
                    onCountrySelectionChange?.(defaultCountry);
                }
            }
        } else {
            // If no allowed countries restriction, use all countries
            setAllowedCountriesData(countriesData);
        }
    }, [countriesData, countryFieldValue]);

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (
                countryButtonRef.current &&
                !countryButtonRef.current.contains(event.target as Node) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setOpenDropdown(false);
            }
        };

        const handleDropdownPosition = () => {
            if (openDropdown && countryButtonRef.current && dropdownRef.current) {
                const dropdownHeight = dropdownRef.current.offsetHeight;
                const triggerRect = countryButtonRef.current.getBoundingClientRect();
                const taskbarHeight = window.innerHeight - window.screen.height;

                if (triggerRect.bottom + dropdownHeight > window.innerHeight - taskbarHeight) {
                    setDropdownPlacement('up');
                } else {
                    setDropdownPlacement('down');
                }
            }
        };

        handleDropdownPosition();

        window.addEventListener('click', handleOutsideClick);
        window.addEventListener('scroll', handleDropdownPosition);

        return () => {
            window.removeEventListener('click', handleOutsideClick);
            window.removeEventListener('scroll', handleDropdownPosition);
        };
    }, [openDropdown]);

    useEffect(() => {
        if (countryFieldValue) {
            const dataToSearch = allowedCountriesData || countriesData;
            const filteredParticularCountriesById =
                dataToSearch &&
                dataToSearch.filter((countries: Country) => countries.id.toString() === countryFieldValue);

            if (filteredParticularCountriesById && filteredParticularCountriesById.length > 0) {
                filteredParticularCountriesById?.map((country) => {
                    setCountryName(country.name);
                    setIso2CountryString?.(country.iso2);
                });
            } else {
                setCountryName('');
            }

            setStateDisabled?.(false);
        } else {
            // Don't reset to "Select Country" if a single country was auto-selected
            if (!(allowedCountriesData && allowedCountriesData.length === 1)) {
                setCountryName('Select Country');
            }
        }
    }, [countriesData, allowedCountriesData, countryFieldValue, setIso2CountryString, setStateDisabled]);

    useEffect(() => {
        if (isNaN(parseFloat(countryFieldValue!)) && countryName === 'Select Country') {
            setFieldValue?.('companyCountryCode', '');
        }
    }, [countryName, setFieldValue, countryFieldValue]);

    useMemo(() => {
        switch (id) {
            case 'countryResult':
                const dataToSearch = allowedCountriesData || countriesData;
                dataToSearch &&
                    dataToSearch.map((country) => {
                        if (country.id.toString() === countryFieldValue) {
                            setIso2CountryString?.(country.iso2);
                        }
                    });
                break;

            default:
                break;
        }
    }, [countriesData, allowedCountriesData, countryFieldValue, id, setIso2CountryString]);

    // Check if dropdown should be shown (only when multiple countries are allowed)
    const shouldShowDropdown = !allowedCountriesData || allowedCountriesData.length > 1;

    return (
        <div className="position-relative">
            {id !== 'countryResult' ? (
                <>
                    {shouldShowDropdown ? (
                        <>
                            <button
                                type="button"
                                ref={countryButtonRef}
                                id="country"
                                disabled={!countryFieldValue && hasCandidate ? false : countryDisabled}
                                className={'form-select cursor-pointer text-start ' + (error ? 'is-invalid' : '')}
                                onClick={() => {
                                    setOpenDropdown(!openDropdown);
                                    setFilterText('');
                                    setTimeout(() => {
                                        filterInputRef.current?.focus();
                                    }, 50);
                                }}
                            >
                                {countryName}
                            </button>
                            {openDropdown && (
                                <div ref={dropdownRef} className={`country-dropdown ${dropdownPlacement} w-100`}>
                                    <div className="sticky-search">
                                        <input
                                            type="search"
                                            ref={filterInputRef}
                                            className="form-control cursor-pointer country-search"
                                            placeholder="Search Country"
                                            value={filterText}
                                            onChange={(e) => {
                                                setFilterText(e.target.value);
                                            }}
                                        />
                                    </div>
                                    <div>
                                        {filteredCountries &&
                                            filteredCountries.map((country) => {
                                                return (
                                                    <li
                                                        onBlur={() => setOpenDropdown(false)}
                                                        className="dropdown-item cursor-pointer fs-14"
                                                        onClick={() => {
                                                            setOpenDropdown(false);
                                                            setStateDisabled?.(false);
                                                            setCityDisabled?.(true);
                                                            setCountryName(country.name);
                                                            setNameOfCountry?.(country.name);
                                                            setIso2CountryString?.(country.iso2);
                                                            setFieldValue?.('companyCountryCode', country.id.toString());
                                                            setFieldValue?.('companyStateCode', '');
                                                            setFieldValue?.('companyCityCode', '');

                                                            onCountrySelectionChange?.(country);
                                                        }}
                                                        key={country.id}
                                                        value={country.id}
                                                    >
                                                        {country.name}
                                                    </li>
                                                );
                                            })}
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        // Show static text when only one country is allowed
                        <div className="form-control text-start">
                            {countryName}
                        </div>
                    )}
                </>
            ) : (
                !hideName && countryName
            )}
        </div>
    );
};

export default CountryDropdown;